import { DateTime } from "luxon";

// A custom notification is sent at a day and time in its own time zone (the
// one chosen next to them), whatever the admin's browser zone is. The API
// schedules the send at ExecuationDate, a UTC instant, and only stores the
// time zone, so these build that instant from the chosen day, time and zone,
// and read the day and time back in the same zone.

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// The zone to read or build a time in: the notification's, or the browser's
// when it has none (or one this browser does not know).
const zoneOf = (timeZone) =>
  timeZone && DateTime.local().setZone(timeZone).isValid
    ? timeZone
    : browserZone();

// The time zone a new custom notification's builder starts from: the admin's
// own browser zone, so a notification they never touch the dropdown for goes
// out at the time they typed in their own zone, not India's. Falls back to
// the old default only when the browser does not report a zone (or reports
// one luxon cannot use).
export const defaultTimezone = () => {
  let zone;
  try {
    zone = browserZone();
  } catch {
    zone = null;
  }
  return zone && DateTime.local().setZone(zone).isValid ? zone : "Asia/Calcutta";
};

// "yyyy-MM-dd" of a day chosen in the date picker (a Date at the start of a
// local day) or kept as text ("yyyy-MM-dd", or an ISO string's date part).
// The text is read as it is: new Date("2026-10-06") is UTC midnight, the day
// before in a browser west of UTC.
export const calendarDay = (date) => {
  if (!date) {
    return null;
  }
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
  }
  if (Number.isNaN(date.getTime?.())) {
    return null;
  }
  return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
};

// ExecuationDate: the UTC instant (ISO text) of the day and time ("HH:mm",
// 24-hour) in the time zone. null when the day or time is missing.
export const toExecutionDate = (date, time, timeZone) => {
  const day = calendarDay(date);
  if (!day || !time) {
    return null;
  }
  const instant = DateTime.fromISO(`${day}T${time}`, {
    zone: zoneOf(timeZone),
  });
  return instant.isValid ? instant.toUTC().toISO() : null;
};

// The day ("yyyy-MM-dd"), the time ("HH:mm", 24-hour) and the date picker's
// value (the start of that day in the browser) of ExecuationDate in the time
// zone.
export const fromExecutionDate = (executionDate, timeZone) => {
  const local = executionDate
    ? DateTime.fromISO(executionDate, { zone: "utc" }).setZone(zoneOf(timeZone))
    : null;
  if (!local?.isValid) {
    return { date: "", time: "", pickerDate: null };
  }
  return {
    date: local.toFormat("yyyy-MM-dd"),
    time: local.toFormat("HH:mm"),
    pickerDate: new Date(local.year, local.month - 1, local.day),
  };
};
